import { useState, useEffect, useContext } from "react";
import styled, { keyframes } from "styled-components";
import API from "../../../API";
import ModalArticle from "./Modal";
import { BsFillPencilFill, BsTrash } from "react-icons/bs";
import { FiPlus } from "react-icons/fi";
import AuthContext from "../../../context/AuthContext";
import AlertDelete from "../../AlertDelete";

const nl2br = require("react-nl2br");

export default function Produits(props) {
  const [modalShow,           setModalShow]          = useState(false);
  const [modalDelete,         setModalDelete]         = useState(false);
  const [articles,            setArticles]            = useState([]);
  const [name,                setName]                = useState("");
  const [description,         setDescription]         = useState("");
  const [price,               setPrice]               = useState("");
  const [article_img,         setImage_url]           = useState();
  const [active,              setActiveItem]          = useState({});
  const { user, authTokens }  = useContext(AuthContext);

  useEffect(() => { refreshArticles(); }, []);

  const refreshArticles = () => {
    API.get(`/api/${props.service}/`)
      .then((res) => setArticles(res.data))
      .catch(console.error);
  };

  const onShowDelete = (item) => { setActiveItem(item); setModalDelete(true); };

  const onDelete = () => {
    API.delete(`/api/${props.service}/${active.id}/`)
      .then(() => refreshArticles())
      .catch(console.error);
    setModalDelete(false);
  };

  const onUpdate = (item) => { setActiveItem(item); setModalShow(true); };

  const setModalAddArticle = () => {
    setActiveItem({});
    setModalShow(true);
  };

  const handlesubmit = (item) => {
    if (item.id) {
      const data = {
        id:          item.id,
        name:        name        || item.name,
        description: description || item.description,
        price:       price       || item.price,
        article_img: article_img || null,
      };
      API.put(`/api/${props.service}/${item.id}/`, data, {
        headers: { "content-type": "multipart/form-data" },
      }).then(() => refreshArticles()).catch(console.error);
    } else {
      const data = { name, description, price, article_img };
      API.post(`/api/${props.service}/`, data, {
        headers: { "content-type": "multipart/form-data" },
      }).then(() => refreshArticles()).catch(console.error);
    }
    setModalShow(false);
    setName(""); setDescription(""); setPrice(""); setImage_url(undefined);
  };

  return (
    <Section>
      {/* Section header */}
      <SectionHeader>
        <CategoryLabel>Notre Carte</CategoryLabel>
        <SectionTitle>{props.title}</SectionTitle>
        <GoldBar />
      </SectionHeader>

      {/* Product grid */}
      <Grid>
        {articles.map((product) => (
          <Card key={product.id}>
            {product.article_img && (
              <CardImageWrap>
                <CardImage src={product.article_img} alt={product.name} />
              </CardImageWrap>
            )}

            <CardBody $hasImage={!!product.article_img}>
              <CardTitle>{product.name}</CardTitle>

              {product.description && (
                <CardDesc>{nl2br(product.description)}</CardDesc>
              )}

              {product.price && (
                <CardPrice>{parseFloat(product.price).toFixed(2)}&nbsp;€</CardPrice>
              )}

              {user && product.name && (
                <AdminActions>
                  <ActionBtn $variant="edit" onClick={() => onUpdate(product)} title="Modifier">
                    <BsFillPencilFill />
                  </ActionBtn>
                  <ActionBtn $variant="delete" onClick={() => onShowDelete(product)} title="Supprimer">
                    <BsTrash />
                  </ActionBtn>
                </AdminActions>
              )}
            </CardBody>

            <AlertDelete
              show={modalDelete && active.id === product.id}
              onHide={() => setModalDelete(false)}
              validateDelete={() => onDelete(product)}
            />
          </Card>
        ))}
      </Grid>

      {/* Add button (admin) */}
      {user && (
        <AddRow>
          <AddBtn onClick={setModalAddArticle}>
            <FiPlus /> Ajouter un article
          </AddBtn>
        </AddRow>
      )}

      {/* Shared modal */}
      <ModalArticle
        show={modalShow}
        onHide={() => setModalShow(false)}
        handleSubmit={() => handlesubmit(active)}
        activeItem={active}
        onChangeName={(e) => setName(e.target.value)}
        onChangeDescription={(e) => setDescription(e.target.value)}
        onChangePrice={(e) => setPrice(e.target.value)}
        onChangeImage={(e) => setImage_url(e.target.files[0])}
        services={props.service}
      />
    </Section>
  );
}

/* ─── Keyframes ─── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Styled Components ─── */

const Section = styled.section`
  padding: 4rem 5vw 5rem;
  background: var(--bg-base);

  /* First section on a carte page must clear the fixed navbar (72 px) */
  &:first-of-type {
    padding-top: calc(72px + 4rem);
  }

  &:nth-child(even) {
    background: var(--bg-surface);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  animation: ${fadeUp} 0.6s ease both;
`;

const CategoryLabel = styled.span`
  display: block;
  font-family: var(--font-script);
  font-size: 1rem;
  color: var(--primary);
  letter-spacing: 0.12em;
  font-style: italic;
  margin-bottom: 0.4rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  color: var(--cream);
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const GoldBar = styled.div`
  width: 50px;
  height: 2px;
  background: linear-gradient(to right, transparent, var(--primary), transparent);
  margin: 1rem auto 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.75rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const Card = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  overflow: hidden;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  animation: ${fadeUp} 0.5s ease both;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(200, 151, 62, 0.35);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
  }
`;

const CardImageWrap = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const CardBody = styled.div`
  padding: 1.25rem 1.5rem ${({ $hasImage }) => ($hasImage ? "1.5rem" : "1.25rem")};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.15rem;
  color: var(--cream);
  font-weight: 500;
  line-height: 1.3;
`;

const CardDesc = styled.p`
  font-size: 0.88rem;
  color: var(--cream-muted);
  line-height: 1.7;
  letter-spacing: 0.02em;
`;

const CardPrice = styled.span`
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 1.2rem;
  color: var(--primary);
  font-weight: 600;
  margin-top: 0.25rem;
`;

const AdminActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-light);
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-sm);
  border: 1px solid
    ${({ $variant }) =>
      $variant === "edit"
        ? "rgba(200, 151, 62, 0.3)"
        : "rgba(220, 50, 50, 0.3)"};
  background: transparent;
  color: ${({ $variant }) =>
    $variant === "edit" ? "var(--primary)" : "#e05555"};
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.85rem;

  &:hover {
    background: ${({ $variant }) =>
      $variant === "edit"
        ? "var(--primary-glow)"
        : "rgba(220, 50, 50, 0.12)"};
    transform: translateY(-1px);
  }
`;

const AddRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: var(--bg-base);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: linear-gradient(135deg, var(--primary-light), var(--primary));
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(200, 151, 62, 0.3);
  }
`;
